import type { IApiResponse } from '../../../services/api';
import { Api } from '../../../services/interceptor';
import { QUERY_KEY } from '../../../services/queryEndPoint';
import { normalizeDivisionsPayload } from '../utils/divisionNormalize';
import type {
  IAttachPlayersRequest,
  ICheckDuplicatesRequest,
  ICheckDuplicatesResponse,
  ICreatedTeamResponse,
  ICreateNewTeamByPlayerRequest,
  ICreateDivisionRequest,
  IDivisionUpdateRequest,
  IDivisionsPayload,
  IMember,
  IMyTeam,
  IMyTeamApiResponse,
  IPlayerSearchResult,
  ITeamGroundSummary,
  ITeamPlayerResponse,
  ITeamSnapshotResponse,
  ITournament,
  ITournamentSearchApiResponse,
  ITournamentSearchResult,
} from '../types/myTeam';

type ApiPayload<T> = T | IApiResponse<T>;

const unwrapApiPayload = <T>(payload: ApiPayload<T>): T => {
  if (
    payload &&
    typeof payload === 'object' &&
    'data' in payload
  ) {
    return payload.data as T;
  }

  return payload as T;
};

export const mapApiTeamToMyTeam = (team: IMyTeamApiResponse): IMyTeam => ({
  teamId: team.teamId,
  teamName: team.teamName,
  teamCode: team.teamCode ?? team.teamShortCode ?? undefined,
  teamLogo: team.imageUrl ?? undefined,
  cricCenterTeamId: team.teamId,
  teamSnapshotId: team.teamSnapshotId,
  description: team.description ?? undefined,
  groundId: (team.groundId as ITeamGroundSummary | null)?.groundId,
  totalPlayers: team.totalPlayers ?? 0,
  tournamentName: team.tournamentName ?? undefined,
  role: team.role ?? undefined,
  canEditTeam: team.canEditTeam,
  canRemovePlayerFromTeam: team.canRemovePlayerFromTeam,
  canAddPlayerToTeam: team.canAddPlayerToTeam,
  canAssignRoleToPlayer: team.canAssignRoleToPlayer,
  captainPlayerId: team.captainPlayerId,
  viceCaptainPlayerId: team.viceCaptainPlayerId,
  wkPlayerId: team.wkPlayerId,
  winMatchesCount: team.winMatchesCount,
  lossMatchesCount: team.lossMatchesCount,
  drawMatchesCount: team.drawMatchesCount,
  recentForm: team.recentForm,
});

type CaptainIds = {
  captainPlayerId?: string | null;
  viceCaptainPlayerId?: string | null;
  wkPlayerId?: string | null;
};

export const mapApiPlayerToMember = (
  player: ITeamPlayerResponse,
  captainIds?: CaptainIds
): IMember => {
  const isCaptain = captainIds?.captainPlayerId != null && player.playerId === captainIds.captainPlayerId;
  const isViceCaptain = captainIds?.viceCaptainPlayerId != null && player.playerId === captainIds.viceCaptainPlayerId;
  return {
    playerId: player.playerId,
    playerCode: player.playerCode ?? undefined,
    playerFirstName: player.playerFirstName ?? '',
    playerLastName: player.playerLastName ?? '',
    role: player.playerRole ?? 'Player',
    isCaptain,
    isViceCaptain,
    avatar: player.imageUri ?? undefined,
  };
};

export const fetchTeamsByTeamAdminId = async (playerId?: string): Promise<IMyTeam[]> => {
  const { data } = await Api.get<ApiPayload<IMyTeamApiResponse[]>>(
    QUERY_KEY.getTeamsByTeamAdminId,
    {
      params: playerId ? { playerId } : undefined,
    }
  );

  return unwrapApiPayload(data).map(mapApiTeamToMyTeam);
};

export const createNewTeamByPlayer = async (
  payload: ICreateNewTeamByPlayerRequest
): Promise<ICreatedTeamResponse> => {
  const { data } = await Api.post<ApiPayload<ICreatedTeamResponse>>(
    QUERY_KEY.createNewTeamByPlayer,
    payload
  );

  return unwrapApiPayload(data);
};

/** League / tournament team edit (Team Info, etc.) — PUT `updateTeam` with full body. */
export interface IUpdateTeamRequest {
  teamId?: string;
  teamSnapshotId?: string;
  teamSnapShotId?: string;
  teamName: string;
  teamCode?: string;
  description?: string;
  image?: ICreateNewTeamByPlayerRequest["image"];
  captainPlayerId?: string | null;
  viceCaptainPlayerId?: string | null;
  wkPlayerId?: string | null;
  tournamentId?: string;
}

export const updateTeam = async (
  payload: IUpdateTeamRequest
): Promise<ICreatedTeamResponse> => {
  const { data } = await Api.put<ApiPayload<ICreatedTeamResponse>>(
    QUERY_KEY.updateTeam,
    payload
  );

  return unwrapApiPayload(data);
};

/** My Team edit — PUT `updateTeamByPlayer/{teamId}` body: teamName, teamCode?, description? */
export interface IUpdateTeamByPlayerRequest {
  teamId: string;
  teamName: string;
  teamCode?: string;
  description?: string;
}

export const updateTeamByPlayer = async (
  payload: IUpdateTeamByPlayerRequest
): Promise<ICreatedTeamResponse> => {
  const teamId = payload.teamId?.trim();
  if (!teamId) {
    throw new Error("teamId is required to update a team");
  }

  const body: { teamName: string; teamCode?: string; description?: string } = {
    teamName: payload.teamName.trim(),
  };
  const code = payload.teamCode?.trim();
  if (code) body.teamCode = code;
  const desc = payload.description?.trim();
  if (desc) body.description = desc;

  const { data } = await Api.put<ApiPayload<ICreatedTeamResponse>>(
    `${QUERY_KEY.updateTeamByPlayer}/${teamId}`,
    body
  );

  return unwrapApiPayload(data);
};

export const fetchTeamPlayers = async (
  teamId: string,
  captainIds?: CaptainIds
): Promise<IMember[]> => {
  const { data } = await Api.get<ApiPayload<ITeamPlayerResponse[]>>(
    QUERY_KEY.getMyTeamPlayers,
    {
      params: { teamId },
    }
  );

  const players = unwrapApiPayload(data);
  return (Array.isArray(players) ? players : []).map((p) => mapApiPlayerToMember(p, captainIds));
};

/** GET /playersByTeamSnapshotId/{teamSnapshotId} - players for a team in a tournament */
export const fetchTeamSnapshotPlayers = async (
  teamSnapshotId: string,
  captainIds?: CaptainIds
): Promise<IMember[]> => {
  const { data } = await Api.get<ApiPayload<ITeamPlayerResponse[]>>(
    `${QUERY_KEY.getAllPlayers}/${teamSnapshotId}`
  );
  const players = unwrapApiPayload(data);
  return (Array.isArray(players) ? players : []).map((p) => mapApiPlayerToMember(p, captainIds));
};

/** DELETE /removePlayerFromTeamSnapshot - remove player from team snapshot (tournament roster) */
export const removePlayerFromTeamSnapshot = async (
  teamSnapshotId: string,
  playerId: string
): Promise<string> => {
  const { data } = await Api.delete<ApiPayload<string>>(QUERY_KEY.removePlayerFromTeamSnapshot, {
    params: { teamSnapshotId, playerId },
  });
  return unwrapApiPayload(data) ?? 'Success';
};

/** POST /addPlayerToTeamSnapshot/{teamSnapshotId}/{playerId} — attaches one existing player to a team snapshot. */
export const addPlayerToTeamSnapshot = async (
  teamSnapshotId: string,
  playerId: string
): Promise<string> => {
  const { data } = await Api.post<string | ApiPayload<string>>(
    `${QUERY_KEY.addPlayerToTeamSnapshot}/${teamSnapshotId}/${playerId}`
  );
  if (typeof data === 'string') return data;
  return unwrapApiPayload(data) ?? 'Success';
};

export const searchPlayers = async (query: string): Promise<IPlayerSearchResult[]> => {
  if (!query.trim()) return [];
  const { data } = await Api.get<ApiPayload<ITeamPlayerResponse[]>>(
    `${QUERY_KEY.searchPlayer}/${encodeURIComponent(query.trim())}`
  );
  const players = unwrapApiPayload(data);
  return players.map((p) => ({
    playerId: p.playerId,
    playerCode: p.playerCode ?? undefined,
    playerFirstName: p.playerFirstName ?? '',
    playerLastName: p.playerLastName ?? '',
    role: p.playerRole ?? undefined,
  }));
};

export const assignCoAdmin = async (
  teamId: string,
  playerIds: string[]
): Promise<string> => {
  const { data } = await Api.post<ApiPayload<string>>(
    `${QUERY_KEY.assignCoAdmin}/${teamId}`,
    { leagueId: null, teamId: null, playerIds }
  );
  return unwrapApiPayload(data) ?? 'admin assigned successfully';
};

export const removeCoAdmin = async (
  teamId: string,
  playerIds: string[]
): Promise<string> => {
  const { data } = await Api.post<ApiPayload<string>>(
    `${QUERY_KEY.removeCoAdmin}/${teamId}`,
    { leagueId: null, teamId: null, playerIds }
  );
  return unwrapApiPayload(data) ?? 'player removed successfully';
};

const mapTeamSnapshotToTournament = (snapshot: ITeamSnapshotResponse): ITournament => {
  const t = snapshot.tournament;
  return {
    tournamentId: t?.tournamentId ?? snapshot.teamSnapshotId,
    tournamentName: t?.tournamentName ?? snapshot.teamName,
    teamSnapshotId: snapshot.teamSnapshotId,
    startDate: t?.startDate ?? '',
    endDate: t?.endDate ?? '',
    location: '',
    status: snapshot.status === 'ACTIVE' ? 'Approved' : (snapshot.status as ITournament['status']) ?? undefined,
  };
};

const extractSnapshotArray = (raw: unknown): ITeamSnapshotResponse[] => {
  if (Array.isArray(raw)) return raw as ITeamSnapshotResponse[];
  if (raw && typeof raw === 'object') {
    const obj = raw as Record<string, unknown>;
    const arr = obj.teamSnapshotResponseList ?? obj.teamSnapshots ?? obj.divisionResponseList ?? obj.data;
    return Array.isArray(arr) ? (arr as ITeamSnapshotResponse[]) : [];
  }
  return [];
};

export const fetchTeamSnapshots = async (teamId: string): Promise<ITournament[]> => {
  const { data } = await Api.get<ApiPayload<ITeamSnapshotResponse[]>>(
    `${QUERY_KEY.teamsSnapShotByTeamId}/${teamId}`
  );
  const raw = unwrapApiPayload(data);
  const snapshots = extractSnapshotArray(raw);
  return snapshots.map(mapTeamSnapshotToTournament);
};

const mapTournamentSearchToResult = (t: ITournamentSearchApiResponse): ITournamentSearchResult => ({
  tournamentId: t.tournamentId,
  tournamentName: t.tournamentName,
  organizer: t.organizerName ?? t.organizerEmailId ?? '',
  leagueId: t.leagueId ?? undefined,
});

export const searchTournaments = async (
  name: string
): Promise<ITournamentSearchResult[]> => {
  if (!name.trim()) return [];
  const { data } = await Api.get<ApiPayload<ITournamentSearchApiResponse[]>>(
    QUERY_KEY.searchTournamentByName,
    { params: { name: name.trim() } }
  );
  const tournaments = unwrapApiPayload(data);
  return (Array.isArray(tournaments) ? tournaments : []).map(mapTournamentSearchToResult);
};

export const fetchDivisions = async (
  tournamentId: string
): Promise<IDivisionsPayload> => {
  const trimmed = tournamentId?.trim();
  if (!trimmed) {
    console.warn('[fetchDivisions] Empty or invalid tournamentId - skipping API call');
    return { divisionResponseList: [] };
  }
  try {
    const url = `${QUERY_KEY.getDivisions}/${trimmed}`;
    const { data } = await Api.get<ApiPayload<IDivisionsPayload>>(url);
    return normalizeDivisionsPayload(unwrapApiPayload(data));
  } catch (err) {
    const axiosErr = err as { response?: { status?: number; data?: unknown }; config?: { url?: string } };
    if (axiosErr?.response?.status === 500) {
      console.error('[fetchDivisions] 500 Internal Server Error', {
        tournamentId: trimmed,
        url: `${QUERY_KEY.getDivisions}/${trimmed}`,
        baseURL: import.meta.env.VITE_API_URL,
        responseData: axiosErr.response?.data,
      });
    }
    throw err;
  }
};

export const joinTournamentAsTeam = async (
  tournamentId: string,
  teamId: string,
  divisionId?: string
): Promise<string> => {
  const params: Record<string, string> = { tournamentId, teamId };
  if (divisionId) params.divisionId = divisionId;
  const { data } = await Api.post<ApiPayload<string>>(
    QUERY_KEY.joinTournamentAsATeam,
    null,
    { params }
  );
  return unwrapApiPayload(data) ?? 'Success';
};

export const createDivision = async (payload: ICreateDivisionRequest): Promise<string> => {
  try {
    const { data } = await Api.post<ApiPayload<string>>(QUERY_KEY.createDivision, payload, {
      headers: { 'Content-Type': 'application/json' },
    });
    return unwrapApiPayload(data) ?? '';
  } catch (err) {
    const axiosErr = err as { response?: { status?: number; data?: unknown } };
    if (axiosErr?.response?.status === 500) {
      console.error('[createDivision] 500 Internal Server Error', {
        payload,
        responseData: axiosErr.response?.data,
      });
    }
    throw err;
  }
};

export const updateDivision = async (payload: IDivisionUpdateRequest): Promise<unknown> => {
  try {
    const { data } = await Api.put<ApiPayload<unknown>>(QUERY_KEY.updateDivision, payload, {
      headers: { 'Content-Type': 'application/json' },
    });
    return unwrapApiPayload(data);
  } catch (err) {
    const axiosErr = err as { response?: { status?: number; data?: unknown } };
    if (axiosErr?.response?.status === 500) {
      console.error('[updateDivision] 500 Internal Server Error', {
        payload,
        responseData: axiosErr.response?.data,
      });
    }
    throw err;
  }
};

export const checkDuplicatesInLeague = async (
  payload: ICheckDuplicatesRequest
): Promise<ICheckDuplicatesResponse> => {
  const { data } = await Api.post<ApiPayload<ICheckDuplicatesResponse>>(
    QUERY_KEY.checkDuplicatesInLeague,
    payload
  );
  return unwrapApiPayload(data);
};

export const attachPlayersToLeague = async (
  payload: IAttachPlayersRequest
): Promise<unknown> => {
  const { data } = await Api.post<ApiPayload<unknown>>(
    QUERY_KEY.teamPlayersJoinAndAttachToLeague,
    payload
  );
  return unwrapApiPayload(data);
};
